import React from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { AlertTriangle, ListOrdered } from "lucide-react";
import Layout from "../Layout";

import step1 from "../../../assets/1.png";
import step2 from "../../../assets/2.png";
import step3 from "../../../assets/3.png";

type Step = {
  id: number;
  title: string;
  desc: string;
  image: string;
};

function DeleteAccountGuide() {
  const { t } = useTranslation();

  const steps: Step[] = [
    {
      id: 1,
      title: t("deleteAccountGuide.step1Title"),
      desc: t("deleteAccountGuide.step1Desc"),
      image: step1,
    },
    {
      id: 2,
      title: t("deleteAccountGuide.step2Title"),
      desc: t("deleteAccountGuide.step2Desc"),
      image: step2,
    },
    {
      id: 3,
      title: t("deleteAccountGuide.step3Title"),
      desc: t("deleteAccountGuide.step3Desc"),
      image: step3,
    },
  ];

  return (
    <Layout title={t("deleteAccountGuide.title")}>
      <div className="min-h-screen bg-gradient-to-br from-background via-muted/30 to-background">
        {/* Hero */}
        <section className="py-8 md:py-12 lg:py-16 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-primary/3 to-primary/5" />
          <div className="absolute top-0 right-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />

          <div className="relative max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-8 md:mb-12"
            >
              <div className="flex justify-center items-center gap-3">
                <div className="p-4 bg-primary/10 rounded-2xl">
                  <ListOrdered className="w-8 h-8 md:w-10 md:h-10 text-primary" />
                </div>
                <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold">
                  <span className="bg-gradient-to-r from-primary via-primary/90 to-primary bg-clip-text text-transparent">
                    {t("deleteAccountGuide.title")}
                  </span>
                </h1>
              </div>
              <p className="mt-4 text-base md:text-lg text-muted-foreground max-w-2xl mx-auto">
                {t("deleteAccountGuide.subtitle")}
              </p>
            </motion.div>
          </div>
        </section>

        {/* Steps */}
        <section className="pb-10 md:pb-14 lg:pb-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
            <div className="grid grid-cols-1 gap-6 md:gap-8">
              {steps.map((s, idx) => (
                <motion.div
                  key={s.id}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.5, delay: idx * 0.05 }}
                  className="bg-card rounded-2xl border-2 border-border/50 shadow-lg overflow-hidden"
                >
                  <div className="grid grid-cols-1 lg:grid-cols-2">
                    {/* Image */}
                    <div className="relative bg-muted/20">
                      <img
                        src={s.image}
                        alt={`step-${s.id}`}
                        className="w-full h-full object-contain max-h-[520px] lg:max-h-[620px] p-4 md:p-6"
                        loading="lazy"
                      />
                      <div className="absolute top-4 md:top-6 left-4 md:left-6">
                        <div className="bg-primary text-onPrimary w-10 h-10 md:w-12 md:h-12 rounded-xl flex items-center justify-center font-bold text-lg shadow-md">
                          {s.id}
                        </div>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="p-6 md:p-8 lg:p-10 flex flex-col justify-center">
                      <h2 className="text-xl md:text-2xl lg:text-3xl font-bold text-foreground mb-3">
                        {s.title}
                      </h2>
                      <p className="text-sm md:text-base lg:text-lg text-muted-foreground leading-relaxed">
                        {s.desc}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Note */}
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.5, delay: 0.15 }}
              className="mt-8 md:mt-10 bg-primary/5 border-2 border-primary/15 rounded-2xl p-5 md:p-6 flex items-start gap-3"
            >
              <div className="p-2.5 bg-primary/10 rounded-xl flex-shrink-0">
                <AlertTriangle className="w-5 h-5 md:w-6 md:h-6 text-primary" />
              </div>
              <div>
                <h3 className="font-bold text-foreground mb-1">
                  {t("deleteAccountGuide.noteTitle")}
                </h3>
                <p className="text-sm md:text-base text-muted-foreground">
                  {t("deleteAccountGuide.noteText")}
                </p>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </Layout>
  );
}

export default DeleteAccountGuide;

